import { Injectable } from '@angular/core';
import { Campaign, CampaignChannelSettings, CampaignStatus } from '@campaign-portal/namespace/entities/campaigns/specs';
import { ChannelType } from '@campaign-portal/namespace/common/enums';
import { MessageTemplatePipe, SenderPipe } from '../repo';
import { AlarisProfileService, AlarisProgressLineType, TagMode, Typecast } from '@campaign-portal/components-library';
import { WhatsappTemplateSettings } from '@campaign-portal/namespace/entities/templates/specs';

@Injectable({
	providedIn: 'root',
})
export class CampaignUtilsService {
	constructor(
		private readonly senderPipe: SenderPipe,
		private readonly messagePipe: MessageTemplatePipe,
		private readonly profile: AlarisProfileService
	) {}

	isPreModeratedChannel(channelType?: ChannelType): boolean {
		const preModerateChannels = this.profile.user.details.preModerateCampaignChannels ?? [];
		return Boolean(channelType && preModerateChannels.includes(channelType));
	}

	selectStatusColor(status?: CampaignStatus): TagMode {
		const statusSet: Record<CampaignStatus, TagMode> = {
			[CampaignStatus.IN_PROGRESS]: 'info',
			[CampaignStatus.COMPLETED]: 'success',
			[CampaignStatus.FAILED]: 'error',
			[CampaignStatus.CANCELLED]: 'gray-light',
			[CampaignStatus.SCHEDULED]: 'gray',
			[CampaignStatus.WAITING_FOR_APPROVAL]: 'gray',
			[CampaignStatus.PAUSED]: 'gray',
		};
		return statusSet[status ?? CampaignStatus.SCHEDULED];
	}

	selectCampaignProgressColor(campaign: Campaign): AlarisProgressLineType {
		switch (campaign.info.status) {
			case CampaignStatus.IN_PROGRESS:
				return 'info';
			case CampaignStatus.COMPLETED:
				return 'success';
			case CampaignStatus.FAILED:
				return 'success-negative';
			default:
				return 'secondary';
		}
	}

	calculatePercentage(campaign: Campaign): number {
		const progress = (campaign.statistics?.progress ?? 0) * 100;
		return Math.max(0, progress);
	}

	prepareTTL(campaign: Campaign, channelType: ChannelType): { seconds: number } | null {
		const channels = campaign.setup.channels || [];
		const omni = channels.find((setting) => setting.channelType === channelType)?.omni ?? {};
		if (omni.ttl) {
			return { seconds: omni.ttl };
		} else {
			return null;
		}
	}

	sender(campaign: Campaign, channelType: ChannelType): string {
		const setup = this.campaignSettings(campaign, channelType);
		return setup === undefined ? '' : (this.senderPipe.transform(setup.sender)?.name ?? '');
	}

	message(campaign: Campaign, channelType: ChannelType): string {
		const setup = this.campaignSettings(campaign, channelType);
		if (Typecast.isString(setup?.message?.text)) {
			return setup.message.text;
		}
		if (Typecast.isNumber(setup?.message?.templateID)) {
			return this.messagePipe.transform(setup.message.templateID)?.messageTemplate ?? '';
		}
		return '';
	}

	imageUrl(campaign: Campaign, channelType: ChannelType): string | undefined {
		const setup = this.campaignSettings(campaign, channelType);
		return setup?.omni?.viberSettings?.imageUrl;
	}

	buttonCaption(campaign: Campaign, channelType: ChannelType): string | undefined {
		const setup = this.campaignSettings(campaign, channelType);
		return setup?.omni?.viberSettings?.buttonCaption;
	}

	buttonActionUrl(campaign: Campaign, channelType: ChannelType): string | undefined {
		const setup = this.campaignSettings(campaign, channelType);
		return setup?.omni?.viberSettings?.buttonActionUrl;
	}

	whatsappSettings(campaign: Campaign, channelType: ChannelType): WhatsappTemplateSettings | undefined {
		const setup = this.campaignSettings(campaign, channelType);
		return setup?.omni?.whatsappSettings;
	}

	private campaignSettings(campaign: Campaign, channelType: ChannelType): CampaignChannelSettings | undefined {
		return campaign.setup.channels.find((setting) => {
			return setting.channelType === channelType;
		});
	}
}
